import { Link } from 'react-router-dom';
import { articles, categories, faqData } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import JsonLd from '@/components/base/JsonLd';
import { useSEO } from '@/hooks/useSEO';

export default function Home() {
  useSEO({
    title: '菲律宾《炽热之夜》电影 — 剧情介绍与观看指南 | 炽热之夜菲律宾电影资讯站',
    description: '菲律宾《炽热之夜》是一部备受关注的菲律宾情色剧情电影，融合爱情、欲望与社会现实。本站提供详细剧情介绍、演员阵容、上映信息及正规观看渠道，带影迷全面了解这部菲律宾经典院线作品。',
    keywords: '菲律宾炽热之夜,炽热之夜电影,菲律宾情色电影,菲律宾剧情片,炽热之夜剧情,菲律宾院线电影,炽热之夜演员',
    canonicalUrl: 'https://chirezhizhiye.ph',
    ogType: 'website',
  });

  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.filter((a) => !a.featured);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '炽热之夜电影资讯站',
    url: 'https://chirezhizhiye.ph',
    description: '专注菲律宾优质电影内容的华语影迷社区，提供《炽热之夜》及相关菲律宾电影的剧情介绍、演员阵容、影评分析与观影指南。',
    inLanguage: 'zh-CN',
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '炽热之夜电影资讯站',
    url: 'https://chirezhizhiye.ph',
    description: '专注菲律宾优质电影内容的华语影迷社区',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@chirezhizhiye.ph',
      contactType: 'customer service',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <JsonLd data={websiteJsonLd} />
      <JsonLd data={organizationJsonLd} />
      <JsonLd data={faqJsonLd} />

      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20background%20with%20soft%20amber%20and%20deep%20crimson%20tones%2C%20elegant%20bokeh%20light%20particles%20floating%20in%20darkness%2C%20subtle%20film%20grain%20texture%20overlay%2C%20sophisticated%20movie%20premiere%20atmosphere%2C%20rich%20warm%20color%20grading%20reminiscent%20of%20arthouse%20cinema%2C%20no%20text%20no%20people%2C%20minimalist%20yet%20dramatic%20composition%20with%20gentle%20light%20gradients%20and%20floating%20dust%20motes&width=1600&height=900&seq=hero-main-bg&orientation=landscape"
          alt="菲律宾《炽热之夜》电影专题 - 炽热之夜菲律宾电影资讯站"
          title="炽热之夜电影 - 菲律宾情色剧情片"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full text-center px-4 md:px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 mb-6">
            <i className="ri-fire-line text-primary-400"></i>
            <span className="text-sm text-primary-200 font-medium">菲律宾院线话题之作</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
            菲律宾《炽热之夜》
          </h1>
          <p className="text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
            一部融合爱情、欲望与社会现实的菲律宾情色剧情力作。<br className="hidden sm:block" />
            深度剧情、实力演员、艺术突破 — 全方位解读这部经典院线作品。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/plot"
              className="px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              探索剧情
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/category/guide"
              className="px-6 py-3 bg-background-50/10 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-md font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              观看指南
              <i className="ri-guide-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              内容分类
            </h2>
            <p className="text-foreground-500 max-w-lg mx-auto">
              从多个维度深入了解《炽热之夜》，找到你最感兴趣的观影视角
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-accent-200/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-3 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                  {cat.name}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {cat.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                精选文章
              </h2>
              <p className="text-sm text-foreground-500">编辑推荐的深度内容</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                最新资讯
              </h2>
              <p className="text-sm text-foreground-500">更多精彩内容持续更新</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {latestArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            常见问题
          </h2>
          <div className="flex flex-col gap-3">
            {faqData.map((item, index) => (
              <details
                key={index}
                className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                  <h3 className="font-heading text-sm md:text-base font-medium text-foreground-800 pr-4">
                    {item.question}
                  </h3>
                  <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full px-4 md:px-6 py-16 md:py-20 bg-background-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20texture%20with%20soft%20amber%20and%20golden%20gradients%2C%20elegant%20light%20streaks%2C%20sophisticated%20film%20noir%20atmosphere%2C%20subtle%20grain%2C%20rich%20warm%20dark%20tones%2C%20no%20text%20no%20people&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            加入炽热之夜影迷社区
          </h2>
          <p className="text-foreground-300 mb-8 leading-relaxed">
            获取最新的菲律宾电影资讯、深度影评和独家幕后内容，与志同道合的影迷一起探索菲律宾电影的独特魅力。
          </p>
          <Link
            to="/about"
            className="inline-flex items-center px-6 py-3 bg-accent-500 text-background-50 rounded-md font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            了解更多
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>
    </>
  );
}